/**
 * <fitment-garage>  — "Fits My Bike" store-wide selector.
 *
 * Year + Model pickers (model list loaded from the fitment-models.json asset).
 * Remembers the shopper's bike in localStorage ("hawg_garage") and, on submit,
 * deep-links into the native Shopify metafield filters (custom.fits_year /
 * custom.fits_model) on the target parts collection. Also renders the current
 * garage chip with change / clear.
 *
 * <fitment-verdict> — product-page companion. Reads the product's fitment JSON
 * (rendered inline) and tells the shopper whether their saved bike is a match.
 *
 * Pure client-side, no dependencies. Storage failures degrade gracefully.
 */
const GARAGE_KEY = 'hawg_garage';

function readGarage() {
  try {
    const raw = localStorage.getItem(GARAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}
function writeGarage(v) {
  try {
    if (v) localStorage.setItem(GARAGE_KEY, JSON.stringify(v));
    else localStorage.removeItem(GARAGE_KEY);
  } catch (e) {
    /* private mode / blocked storage — ignore */
  }
  document.dispatchEvent(new CustomEvent('garage:change', { detail: v }));
}
function garageLabel(g) {
  if (!g) return '';
  return [g.year, g.model].filter(Boolean).join(' ');
}

class FitmentGarage extends HTMLElement {
  connectedCallback() {
    this.target = this.dataset.target || '/collections/all-parts';
    this.dataUrl = this.dataset.models;
    this.yearSel = this.querySelector('[data-garage-year]');
    this.modelInput = this.querySelector('[data-garage-model]');
    this.modelList = this.querySelector('[data-garage-modellist]');
    this.form = this.querySelector('[data-garage-form]');
    this.chip = this.querySelector('[data-garage-chip]');

    this.#loadData();

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#go();
    });
    this.querySelector('[data-garage-clear]')?.addEventListener('click', (e) => {
      e.preventDefault();
      writeGarage(null);
      this.#renderChip();
      if (this.yearSel) this.yearSel.value = '';
      if (this.modelInput) this.modelInput.value = '';
    });
    document.addEventListener('garage:change', () => this.#renderChip());
    this.#renderChip();
  }

  async #loadData() {
    // Populate year options immediately from a sensible range so the control is
    // usable even before the model list resolves.
    let years = [];
    for (let y = new Date().getFullYear() + 1; y >= 1970; y--) years.push(y);
    this.#fillYears(years);

    if (!this.dataUrl) return;
    try {
      const res = await fetch(this.dataUrl, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.years) && data.years.length) this.#fillYears(data.years);
      this.models = Array.isArray(data.models) ? data.models : [];
      if (this.modelList) {
        this.modelList.innerHTML = this.models
          .map((m) => `<option value="${m.replace(/"/g, '&quot;')}"></option>`)
          .join('');
      }
      // Pre-fill from a saved garage.
      const g = readGarage();
      if (g) {
        if (this.yearSel && g.year) this.yearSel.value = g.year;
        if (this.modelInput && g.model) this.modelInput.value = g.model;
      }
    } catch (e) {
      /* asset fetch failed — year-only garage still works */
    }
  }

  #fillYears(years) {
    if (!this.yearSel) return;
    const cur = this.yearSel.value;
    const first = this.yearSel.querySelector('option[value=""]');
    this.yearSel.innerHTML = '';
    if (first) this.yearSel.appendChild(first);
    else {
      const o = document.createElement('option');
      o.value = ''; o.textContent = 'Year';
      this.yearSel.appendChild(o);
    }
    years.forEach((y) => {
      const o = document.createElement('option');
      o.value = String(y); o.textContent = y;
      this.yearSel.appendChild(o);
    });
    if (cur) this.yearSel.value = cur;
  }

  #go() {
    const year = this.yearSel?.value?.trim() || '';
    const model = this.modelInput?.value?.trim() || '';
    if (!year && !model) return;

    writeGarage({ year, model, make: 'Harley-Davidson' });

    const params = new URLSearchParams();
    const known = this.models && this.models.some((m) => m.toLowerCase() === model.toLowerCase());
    if (year) params.set('filter.p.m.custom.fits_year', year);
    if (model && known) {
      // Exact model → native metafield filter.
      params.set('filter.p.m.custom.fits_model', model);
      window.location.href = `${this.target}?${params.toString()}`;
    } else if (model) {
      // Free text that isn't an exact model → keyword search, keep year filter.
      const q = encodeURIComponent(`${model}`);
      const yr = year ? `&filter.p.m.custom.fits_year=${encodeURIComponent(year)}` : '';
      window.location.href = `/search?q=${q}&type=product${yr}`;
    } else {
      window.location.href = `${this.target}?${params.toString()}`;
    }
  }

  #renderChip() {
    if (!this.chip) return;
    const g = readGarage();
    if (!g || (!g.year && !g.model)) {
      this.chip.hidden = true;
      return;
    }
    this.chip.hidden = false;
    const label = this.chip.querySelector('[data-garage-label]');
    if (label) label.textContent = garageLabel(g);
  }
}

class FitmentVerdict extends HTMLElement {
  connectedCallback() {
    let fit;
    try {
      fit = JSON.parse(this.querySelector('[data-fit-json]')?.textContent || 'null');
    } catch (e) {
      fit = null;
    }
    this.fit = fit;
    this.out = this.querySelector('[data-verdict-out]');
    this.render();
    document.addEventListener('garage:change', () => this.render());
  }

  render() {
    if (!this.out || !this.fit || !Array.isArray(this.fit.models)) {
      this.hidden = true;
      return;
    }
    const g = readGarage();
    if (!g || (!g.year && !g.model)) {
      this.hidden = true;
      return;
    }
    const yr = g.year ? Number(g.year) : null;
    const ml = (g.model || '').toLowerCase();

    const expand = (s) => {
      const set = new Set();
      String(s || '').split(',').forEach((part) => {
        const p = part.trim();
        if (!p) return;
        if (p.includes('-')) {
          const [a, b] = p.split('-').map(Number);
          for (let y = a; y <= b; y++) set.add(y);
        } else set.add(Number(p));
      });
      return set;
    };

    const match = this.fit.models.find((m) => {
      const okModel = !ml || m.model.toLowerCase() === ml || m.model.toLowerCase().includes(ml);
      const okYear = !yr || expand(m.years).has(yr);
      return okModel && okYear;
    });

    this.hidden = false;
    const label = [g.year, g.model].filter(Boolean).join(' ') || 'your bike';
    if (match) {
      this.out.className = 'fitment-verdict fitment-verdict--yes';
      this.out.innerHTML = `<span class="fitment-verdict__icon" aria-hidden="true"></span> Fits your ${label}`;
    } else {
      this.out.className = 'fitment-verdict fitment-verdict--no';
      this.out.textContent = `Not listed for your ${label} — verify before buying.`;
    }
  }
}

if (!customElements.get('fitment-garage')) customElements.define('fitment-garage', FitmentGarage);
if (!customElements.get('fitment-verdict')) customElements.define('fitment-verdict', FitmentVerdict);
